import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {api} from '@/lib/api'
import type {components} from '@/lib/api-schema'

export type CambiosOcupacion = components['schemas']['Page_CambioOcupacionRead_']
export type SugerenciaCambio = components['schemas']['CambioOcupacionRead']
export type ResumenCambios = components['schemas']['CambiosOcupacionResumenRead']

export const BANDEJA_PAGE_SIZE = 20

export function useCambiosOcupacion(businessId: string | undefined, page: number) {
  return useQuery({
    queryKey: ['cambios-ocupacion', businessId, page],
    queryFn: async () =>
      (
        await api.get<CambiosOcupacion>(`/businesses/${businessId}/cambios-ocupacion`, {
          params: {page, page_size: BANDEJA_PAGE_SIZE},
        })
      ).data,
    enabled: !!businessId,
    placeholderData: (previous) => previous,
  })
}

/** Se lee aparte para que el chip de la pestaña no dependa de la página que se esté mirando. */
export function useResumenCambios(businessId: string | undefined) {
  return useQuery({
    queryKey: ['cambios-ocupacion-resumen', businessId],
    queryFn: async () => (await api.get<ResumenCambios>(`/businesses/${businessId}/cambios-ocupacion/resumen`)).data,
    enabled: !!businessId,
  })
}

export type HistorialCambios = components['schemas']['Page_CambioOcupacionRead_']

export const HISTORIAL_PAGE_SIZE = 20

export function useHistorialCambios(businessId: string | undefined, enabled: boolean, page: number) {
  return useQuery({
    queryKey: ['cambios-ocupacion-historial', businessId, page],
    queryFn: async () =>
      (
        await api.get<HistorialCambios>(`/businesses/${businessId}/cambios-ocupacion/historial`, {
          params: {page, page_size: HISTORIAL_PAGE_SIZE},
        })
      ).data,
    enabled: !!businessId && enabled,
    placeholderData: (previous) => previous,
  })
}

/** La respuesta es la PRIMERA página: se escribe bajo esa clave y se invalidan las demás. No
 * invalida `examenes-matriz`: la rejilla no lee esta tabla. */
function useDecisionMutation(businessId: string, accion: 'confirmar' | 'descartar') {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {sugerenciaId: string; motivo?: string}) =>
      (
        await api.post<CambiosOcupacion>(
          `/businesses/${businessId}/cambios-ocupacion/${input.sugerenciaId}/${accion}`,
          input.motivo === undefined ? undefined : {motivo: input.motivo}
        )
      ).data,
    onSuccess: (data) => {
      queryClient.setQueryData(['cambios-ocupacion', businessId, 1], data)
      queryClient.invalidateQueries({
        queryKey: ['cambios-ocupacion', businessId],
        predicate: (query) => query.queryKey[2] !== 1,
      })
      queryClient.invalidateQueries({queryKey: ['cambios-ocupacion-resumen', businessId]})
      queryClient.invalidateQueries({queryKey: ['cambios-ocupacion-historial', businessId]})
    },
  })
}

export function useConfirmarCambio(businessId: string) {
  return useDecisionMutation(businessId, 'confirmar')
}

export function useDescartarCambio(businessId: string) {
  return useDecisionMutation(businessId, 'descartar')
}
